import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as apiMethods from '../res/apiMethods.js';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';

const OP_COLORS = {
  PAGO_PROCESADO:     '#2e7d32',
  LIQUIDACION:        '#1565c0',
  ACTUALIZAR_CREDITO: '#e65100',
  CREAR_CREDITO:      '#6a1b9a',
  ELIMINAR_CREDITO:   '#b71c1c',
  CIERRE_PERIODO:     '#37474f',
};
const opColor = (op) => OP_COLORS[op] || '#546e7a';

const formatTs = (ts) => {
  if (!ts) return '—';
  try {
    return new Date(ts).toLocaleString('es-CO', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch { return ts; }
};

const LogRow = ({ entry, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr style={{ background: open ? '#F8FAFC' : undefined }}>
        <td className="tabular-nums" style={{ whiteSpace: 'nowrap', fontSize: '0.84rem', color: '#475467' }}>
          {formatTs(entry.timestamp)}
        </td>
        <td>
          <Chip
            label={entry.operation || '—'}
            size="small"
            sx={{
              backgroundColor: opColor(entry.operation),
              color: '#fff',
              fontWeight: 600,
              fontSize: '0.72rem',
              borderRadius: '6px'
            }}
          />
        </td>
        <td style={{ fontWeight: 500, color: '#101828' }}>
          {entry.username || entry.userId || '—'}
        </td>
        <td className="tabular-nums" style={{ color: '#475467' }}>
          {entry.creditoId || '—'}
        </td>
        <td style={{ textAlign: 'center' }}>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            sx={{
              color: '#64748B',
              '&:hover': { color: '#D71920', background: 'rgba(215, 25, 32, 0.08)' }
            }}
          >
            {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={5} style={{ padding: 0, borderBottom: '1px solid #EAECF0' }}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <div style={{ margin: '0.75rem 1.25rem 1rem', padding: '1rem 1.25rem', backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '10px', overflowX: 'auto' }}>
                <pre style={{ margin: 0, fontSize: '0.8rem', color: '#1E293B', fontFamily: '"Consolas", monospace', lineHeight: 1.6 }}>
                  {JSON.stringify(entry, null, 2)}
                </pre>
              </div>
            </Collapse>
          </td>
        </tr>
      )}
    </>
  );
};

const Logs = () => {
  const [lines, setLines] = useState(200);
  const [operacion, setOperacion] = useState('');
  const [creditoId, setCreditoId] = useState('');
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(null);
  const [operaciones, setOperaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const token = window.sessionStorage.getItem('localToken');
    const username = window.sessionStorage.getItem('username');
    if (!token) { window.location.href = '/login'; return; }
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      if (!(payload.roleId === 1 && username === 'complemento360')) {
        window.location.href = '/access-denied';
        return;
      }
    } catch {
      window.location.href = '/accessDenied';
      return;
    }
    (async () => {
      try {
        const res = await axios.get(apiMethods.GET_LOGS_OPERACIONES, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.code === 0) setOperaciones(res.data.operaciones || []);
      } catch { /* ignore */ }
    })();
  }, []);

  const fetchLogs = async () => {
    const token = window.sessionStorage.getItem('localToken');
    setLoading(true);
    try {
      const params = {};
      if (lines !== '' && lines !== null) params.lines = Number(lines);
      if (operacion) params.operacion = operacion.toUpperCase();
      if (creditoId.trim()) params.creditoId = creditoId.trim();
      const res = await axios.get(apiMethods.GET_LOGS, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data?.code === 0) {
        // Más reciente primero (el backend devuelve las últimas N líneas en orden cronológico)
        setEntries([...(res.data.entries || [])].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
        setTotal(res.data.total ?? (res.data.entries || []).length);
      } else {
        setEntries([]);
        setTotal(0);
      }
    } catch {
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const downloadLogs = async () => {
    const token = window.sessionStorage.getItem('localToken');
    try {
      const res = await axios.get(apiMethods.GET_LOGS_DOWNLOAD, {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/plain' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'logs.txt';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const handleKeyDown = (e) => { if (e.key === 'Enter') fetchLogs(); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header de la sección */}
      <div className="section-header">
        <div className="title-group">
          <h1>Auditoría y Logs del Sistema</h1>
          <p>Trazabilidad histórica de operaciones, transacciones y eventos de seguridad</p>
        </div>
        <button
          type="button"
          className="btn-mobilia-secondary"
          onClick={downloadLogs}
        >
          <DownloadIcon style={{ fontSize: '1rem' }} /> Descargar Archivo Logs
        </button>
      </div>

      {/* Panel de Filtros */}
      <div className="glass-panel" style={{ padding: '1.5rem 1.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1.25rem' }}>
          <FilterListIcon sx={{ color: '#D71920', fontSize: '1.2rem' }} />
          <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: '#101828' }}>
            Filtros de Búsqueda
          </h3>
        </div>

        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label="Líneas (0 = todas)"
            type="number"
            size="small"
            value={lines}
            onChange={e => setLines(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ width: 160 }}
            inputProps={{ min: 0 }}
          />

          <FormControl size="small" style={{ minWidth: 220 }}>
            <InputLabel>Operación</InputLabel>
            <Select
              value={operacion}
              label="Operación"
              onChange={e => setOperacion(e.target.value)}
            >
              <MenuItem value=""><em>-- Todas --</em></MenuItem>
              {operaciones.map(op => (
                <MenuItem key={op} value={op}>{op}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Crédito Id"
            size="small"
            value={creditoId}
            onChange={e => setCreditoId(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{ width: 180 }}
          />

          <button
            type="button"
            className="btn-mobilia-primary"
            onClick={fetchLogs}
            disabled={loading}
          >
            <SearchIcon style={{ fontSize: '1.05rem' }} />
            {loading ? 'Consultando…' : 'Consultar Logs'}
          </button>
        </div>
      </div>

      {/* Resultados */}
      {searched && (
        <div className="glass-panel" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, margin: 0, color: '#101828' }}>
              Registros Obtenidos
            </h3>
            <span className="badge-pill-navy">
              Total: {total ?? entries.length} eventos
            </span>
          </div>

          {entries.length === 0 ? (
            <div style={{ padding: '2rem 1rem', textAlign: 'center', color: '#667085', fontSize: '0.9rem' }}>
              No se encontraron registros de logs con los criterios seleccionados.
            </div>
          ) : (
            <div className="modern-table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th style={{ width: '200px' }}>Timestamp</th>
                    <th style={{ width: '220px' }}>Operación</th>
                    <th>Usuario</th>
                    <th>Crédito Id</th>
                    <th style={{ width: '60px', textAlign: 'center' }}>Detalle</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry, i) => (
                    <LogRow key={i} entry={entry} index={i} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Logs;
